/*
 * liveWireContract.ts — the exact JSON the Graphy console API emits, per endpoint.
 *
 * STANDALONE BY CONSTRUCTION: imports nothing. Never imports LivePreview fixtures
 * (`fixtures`, `fixtureFacts`) or derivation code (`derive`, `previewRankingPolicy`) — the wire
 * shape is backend truth and may not be defined in terms of preview data.
 *
 * snake_case because Rails builders emit snake_case. The adapter maps to the camelCase view
 * model; that mapping is the only place the two vocabularies meet.
 *
 * NO EXPLANATION VOCABULARY. A closed explanation_key/signals enum was drafted and REMOVED: its
 * terms had no product authority, and the current RankingPolicy is not a safe source either — it
 * still carries concepts the accepted r1 contract rejects or treats differently (Preferred Pool,
 * total_score). The wire carries authorized ranking FACTS and closed machine keys only.
 *
 * That is a rule about who owns the WORDS, never a licence to show the operator nothing.
 * liveWireAdapter.ts composes every sentence on the screen from these facts — the `why` column,
 * the results headline, the candidate signal chips, the failure sentence, and a real label for
 * every member of every closed key set here (decisions, timeline steps, exclusions, attention
 * reasons, system alerts). "Unknown" is reserved for a fact the SERVER could not substantiate — a
 * null count, an unparseable instant — and is never the answer to "no label has been written yet".
 * A raw machine key must never reach the screen; an unexpected key is humanised, not echoed.
 *
 * SERVER FACT vs PRESENTATION. This contract carries server FACTS only. Display strings the
 * frontend composes — stateLabel, tone, decisionLabel, headline, stopConfirm copy, the ranking
 * explainer, `why` sentences, signal chips, the diagnostics data-boundary note, "4.2 mi",
 * "Unknown" — are NOT wire fields. Persisting rendered prose would freeze today's copy into
 * permanent history and make it unlocalisable.
 *
 * WORKER IDENTITY. No real worker name crosses this boundary. The server emits `worker_id` and
 * an internal label derived from it (`Worker #<id>`); the adapter maps that to the accepted
 * `displayName`. See INTERNAL_WORKER_LABEL below — the transformation is explicit and tested.
 */

// ---------------------------------------------------------------- primitives

/** Missing evidence. null renders "Unknown" and NEVER 0. A proven zero is emitted as 0. */
export type WireNullableCount = number | null;
export type WireIsoTimestamp = string;

/**
 * An IANA timezone name, e.g. "America/Chicago" — the job address's zone, sourced on the backend
 * from `Job#timezone` (system default as the fallback). Time fields stay UTC ISO 8601 with `Z`;
 * this is the zone the console RENDERS them in, so an operator never reads a shift on their own
 * browser clock instead of the venue's.
 */
export type WireIanaTimezone = string;

/**
 * The earliest instant a metric family has data for. null means the server cannot substantiate
 * ANY value for it, which the UI must render as Unknown rather than as an empty result.
 */
export type WireDataAvailableFrom = WireIsoTimestamp | null;

// --------------------------------------------------------------- closed sets

export const WIRE_MODES = ['live', 'shadow', 'off'] as const;
export type WireMode = (typeof WIRE_MODES)[number];

export const WIRE_JOB_STATES = [
  'watching',
  'shadow_selected',
  'offers_sent',
  'filled',
  'needs_attention',
  'skipped',
  'failed',
] as const;
export type WireJobState = (typeof WIRE_JOB_STATES)[number];

export const WIRE_WORKER_ELIGIBILITIES = [
  'selected',
  'selectable',
  'unselectable_unknown',
] as const;
export type WireWorkerEligibility = (typeof WIRE_WORKER_ELIGIBILITIES)[number];

/** Actions only. A merely-eligible worker carries null, not a member of this set. */
export const WIRE_ACTION_DECISIONS = [
  'offer',
  'will_offer',
  'accepted',
  'closed',
  'withdrawn',
] as const;
export type WireActionDecision = (typeof WIRE_ACTION_DECISIONS)[number];

export const WIRE_EXCLUSION_KEYS = [
  'distance',
  'overlap',
  'blocked',
  'not_suspended',
  'lta_elsewhere',
  'certificates',
  'already_offered',
  'background_check',
  'ssn_compliance',
  'tax_type_mismatch',
  'already_hired',
  'coordinates',
] as const;
export type WireExclusionKey = (typeof WIRE_EXCLUSION_KEYS)[number];

export const WIRE_CHECK_KEYS = [
  'single_open_slot',
  'tier_sent',
  'unfilled',
  'no_ops_offer',
  'lead_time',
] as const;
export type WireCheckKey = (typeof WIRE_CHECK_KEYS)[number];

/**
 * Why one job needs a human. Delivery states (`push_*`, `sms_no_phone`) describe an invitation
 * that WAS created and then could not reach the worker; the two failure reasons describe an
 * attempt that never became an invitation at all (`outcome = 'failed'` on the backend).
 */
export const WIRE_ATTENTION_REASON_CLASSES = [
  'push_failed',
  'push_no_device',
  'sms_no_phone',
  'transport_error',
  'invalid_offer',
] as const;
export type WireAttentionReason =
  (typeof WIRE_ATTENTION_REASON_CLASSES)[number];

export const WIRE_ATTENTION_RESOLUTIONS = [
  'open',
  'acknowledged',
  'archived',
] as const;
export type WireAttentionResolution =
  (typeof WIRE_ATTENTION_RESOLUTIONS)[number];

export const WIRE_SYSTEM_ALERT_KEYS = [
  'scheduler_stale',
  'configuration_unknown',
  'invariant_breach',
  'scheduler_failing',
] as const;
export type WireSystemAlertKey = (typeof WIRE_SYSTEM_ALERT_KEYS)[number];

export const WIRE_FUNNEL_STAGES = [
  'discovered',
  'after_gates',
  'selected',
] as const;
export type WireFunnelStage = (typeof WIRE_FUNNEL_STAGES)[number];

export const WIRE_ERROR_CODES = ['evaluation_error'] as const;
export type WireErrorCode = (typeof WIRE_ERROR_CODES)[number];

/**
 * The steps a recorded run persists, in the order it reaches them — graphy's job_detail_builder
 * TIMELINE_KEYS (discovered, triggered, evaluated, completed) plus its OFFER_CREATED entry, which
 * appears once per offer the run created. Declared closed so the adapter can be proven to have a
 * sentence for every one of them (F4: the timeline used to render these raw keys straight to the
 * operator), and so graphy's drift spec can prove the builder emits nothing outside this set.
 *
 * WireTimelineEntry.key stays typed `string` rather than narrowing to this union on purpose. The
 * backend owns the step list and may persist a new step before this contract catches up; a
 * narrowed type would make such a payload unrepresentable, and the honest handling of an
 * unexpected step is to humanise it (liveWireAdapter.timelineLabelFor), not to fail or to drop it.
 */
export const WIRE_TIMELINE_KEYS = [
  'discovered',
  'triggered',
  'evaluated',
  'completed',
  'offer_created',
] as const;
export type WireTimelineKey = (typeof WIRE_TIMELINE_KEYS)[number];

/**
 * Why the runner declined to offer to a worker it had otherwise reached. `job_blocked` and
 * `worker_blocked` aggregate every Preconditions job/worker blocker that is not one of the named
 * guards, so the closed set stays small without silently dropping a skip.
 */
export const WIRE_GUARD_REASONS = [
  'wave_budget_exhausted',
  'worker_offer_frequency',
  'hourly_ceiling',
  'trigger_stale',
  'quiet_hours',
  'job_blocked',
  'worker_blocked',
] as const;
export type WireGuardReason = (typeof WIRE_GUARD_REASONS)[number];

export const WIRE_POLICY_STATUSES = [
  'draft',
  'active',
  'superseded',
  'rolled_back',
] as const;
export type WirePolicyStatus = (typeof WIRE_POLICY_STATUSES)[number];

// -------------------------------------------------------------- safety state

/**
 * Closed union. `unknown` can NEVER collapse into `clear` — absent proof is not a pass.
 *
 * SEMANTICS:
 *   - `clear` must always carry a closed machine basis, so it can never be asserted bare, and
 *     each basis means exactly one thing (see WIRE_SAFETY_CLEAR_BASES below).
 *   - unreadable configuration or guards => `unknown`.
 *   - effective Shadow => `unknown`, UNLESS a complete persisted Shadow-invariant proof is
 *     explicitly reused and tested. That proof is now wired: graphy's
 *     FirstResponder::ShadowProof is the single authority, consumed by both ConsoleQuery#safety
 *     and CommandCenter#shadow_invariant so the two endpoints cannot disagree.
 *   - a Shadow run that actually created an offer or sent a push => `breached`, carrying the real
 *     persisted reasons. Never `clear`, and never silently downgraded to `unknown`.
 *   - effective LIVE => `clear` only with the `live_bounded_action_proof` basis (Task 6's
 *     persisted per-job/per-wave bound proof), otherwise `unknown`.
 *   - Empty tables are never safety evidence. Nothing observed is not the same as nothing
 *     happened — the backend enforces this by requiring at least one run that actually reached
 *     ranking (run_state 'shadow_selected'), not merely the absence of offers.
 */
export const WIRE_SAFETY_STATES = ['clear', 'unknown', 'breached'] as const;
export type WireSafetyState = (typeof WIRE_SAFETY_STATES)[number];

/**
 * Closed machine values. A basis is required so `clear` can never be asserted bare.
 *
 *   effective_off              - the guards prevent automated worker contact. Nothing CAN happen.
 *   shadow_zero_action_proof   - persisted, mode-scoped Shadow runs reached ranking and produced
 *                                no worker-facing action. Something DID run and nothing happened.
 *   live_bounded_action_proof  - live actions bounded by persisted proof: LIVE ran, and every
 *                                worker-facing action it took is accounted for by Task 6's
 *                                persisted per-job/per-wave proof (never more than
 *                                max_offers_per_job, never a second wave, control arm untouched).
 *
 * These are deliberately separate. They are different claims, and using effective_off to describe
 * a running pilot would be a lie on the wire.
 */
export const WIRE_SAFETY_CLEAR_BASES = [
  'effective_off',
  'shadow_zero_action_proof',
  'live_bounded_action_proof',
] as const;
export type WireSafetyClearBasis = (typeof WIRE_SAFETY_CLEAR_BASES)[number];

export type WireSafetyStatus =
  | { state: 'clear'; basis: WireSafetyClearBasis }
  | { state: 'unknown'; reason: string }
  | { state: 'breached'; reasons: string[] };

// ---------------------------------------------------------------- pagination

/** Server-side. Ordering always carries a deterministic final tie-break. */
export type WirePagination = {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
};

export type WirePage<T> = { items: T[]; pagination: WirePagination };

// ------------------------------------------------------------ worker identity

/**
 * The ONLY worker label that crosses this boundary. Derived purely from the id, so no personal
 * data is transmitted and no name can leak into a cached payload or a screenshot.
 */
export const INTERNAL_WORKER_LABEL_PREFIX = 'Worker #';
export const internalWorkerLabel = (workerId: number): string =>
  `${INTERNAL_WORKER_LABEL_PREFIX}${workerId}`;

// -------------------------------------------------------------------- policy

export type WirePolicyClient = { customer_id: number; name: string };

/** Server-owned validation bounds. The console validates against THESE, never a literal. */
export type WirePolicyConstraints = {
  min_lead_floor_minutes: number;
  wait_minimum_minutes: number;
  max_offers_minimum: number;
  distance_limit_minimum_miles: number;
  treatment_percent_min: number;
  treatment_percent_max: number;
  minimum_clients_for_activation: number;
};

export type WirePolicy = {
  version: string;
  status: WirePolicyStatus;
  based_on_version: string | null;
  clients: WirePolicyClient[];
  wait_before_evaluation_minutes: number;
  min_lead_minutes: number;
  max_offers_per_job: number;
  distance_limit_miles: number;
  treatment_percent: number;
  activated_at: WireIsoTimestamp | null;
};

export type WirePolicyDraft = {
  policy: WirePolicy;
  status: 'draft';
  based_on_version: string | null;
};

export type WirePolicyAudit = {
  occurred_at: WireIsoTimestamp;
  who: string;
  setting: string;
  from_value: string | null;
  to_value: string | null;
  reason: string;
};

// ------------------------------------------------------- immutable snapshots

/** A recorded run is IMMUTABLE. policy_version is the version persisted AT RUN TIME. */
export type WireRunMeta = {
  run_id: string;
  evaluated_at: WireIsoTimestamp;
  policy_version: string;
};

export type WireCheckObservedKind =
  | 'duration_min'
  | 'count'
  | 'timestamp'
  | 'none';

/** Exactly one typed field is populated, chosen by kind. `none` populates neither. */
export type WirePersistedCheck = {
  key: WireCheckKey;
  passed: boolean;
  observed_kind: WireCheckObservedKind;
  observed_number: number | null;
  observed_at: WireIsoTimestamp | null;
  recorded_at: WireIsoTimestamp;
  run_id: string;
};

/** Snapshot facts. action_decision is null unless the worker was actually acted on. */
export type WireCandidate = {
  worker_id: number;
  gate_rank: number;
  eligibility: WireWorkerEligibility;
  selection_rank: number | null;
  action_decision: WireActionDecision | null;
  tier: string | null;
  score: number | null;
  distance_miles: number | null;
  favorite: boolean;
  shifts_with_client: number | null;
};

/**
 * A member of the recorded SELECTED wave. Distinct from WireCandidate on purpose: both required
 * fields are narrowed, so a row that is not genuinely selected cannot be placed here at all.
 *
 * The previous shape was WireCandidate[], and the adapter filtered out null selection_rank rows
 * to make it work. That filter could silently drop a malformed row, leaving the modal's list
 * shorter than the run's selected_count — the selected-count/list-count mismatch class Rav hit.
 * Narrowing the TYPE means an invalid row fails the contract instead of disappearing.
 */
export type WireSelectedCandidate = Omit<
  WireCandidate,
  'eligibility' | 'selection_rank'
> & {
  eligibility: 'selected';
  selection_rank: number;
};

/**
 * `supported: true` means the gate WAS APPLIED to this job, so `count` is an integer and 0 is a
 * real, reportable answer ("this gate excluded nobody"). `supported: false` means the gate was not
 * applied to this job at all and `count` stays null — the UI must say so rather than implying a
 * zero. `tax_type_mismatch` is always false: it is applied before discovery and never counted.
 * `ssn_compliance` is false unless the backend actually applied it.
 */
export type WireExclusionGroup = {
  key: WireExclusionKey;
  count: WireNullableCount;
  supported: boolean;
};

export type WireFunnel = {
  pool_size: WireNullableCount;
  passed_gates: WireNullableCount;
  selected_count: number;
};

export type WireTimelineEntry = { at: WireIsoTimestamp | null; key: string };

export type WireAttentionEvent = {
  job_id: string;
  worker_id: number;
  reason_class: WireAttentionReason;
  raised_at: WireIsoTimestamp;
  resolution: WireAttentionResolution;
  resolved_at: WireIsoTimestamp | null;
};

// ------------------------------------------------------------------- console

export type WireHeadlineStats = {
  watching_now: WireNullableCount;
  offers_sent: WireNullableCount;
  shifts_filled: WireNullableCount;
  needs_attention: WireNullableCount;
};

export type WireClientFilter = {
  key: string;
  customer_id: number | null;
  watching: WireNullableCount;
  filled: WireNullableCount;
};

/**
 * `since` is the REPORTING WINDOW start (the rolling 30-day cutoff these counts were aggregated
 * over), NOT the instant the pilot was enabled. The two were conflated by the console until a
 * review caught the results heading claiming "since enabled Aug 16" for a pilot enabled that same
 * morning. `enabled_at` is the real enable instant, and is null while the pilot is off.
 */
export type WireResultsSummary = {
  since: WireIsoTimestamp | null;
  enabled_at: WireIsoTimestamp | null;
  shifts_acted_on: WireNullableCount;
  filled_by_first_responder: WireNullableCount;
  offers_sent: WireNullableCount;
  accepted: WireNullableCount;
  no_shows_or_drops: WireNullableCount;
  failed_actions: WireNullableCount;
  metabase_url: string | null;
};

export type WireSystemAlert = { key: WireSystemAlertKey; detail: string };

/** One guard, and how many offer attempts it stopped in the reporting window. */
export interface WireGuardSkip {
  reason: WireGuardReason;
  count: WireNullableCount;
}

export type WireDiagnostics = {
  offers_created: WireNullableCount;
  push_sent: WireNullableCount;
  sms_sent: WireNullableCount;
  observed_modes: WireMode[];
  system_alerts: WireSystemAlert[];
  /**
   * Offer attempts with `outcome = 'skipped'` in the reporting window, mode-scoped like the other
   * diagnostics and grouped by guard. null when the mode has no attempts at all — unknown, not a
   * proven absence of skips.
   */
  guard_skips: WireGuardSkip[] | null;
};

/** Requested is configured intent; effective is what the guards permitted. */
export type WireModeState = {
  requested_mode: WireMode;
  effective_mode: WireMode;
};

export type WireDataBoundaries = {
  stats: WireDataAvailableFrom;
  results: WireDataAvailableFrom;
  diagnostics: WireDataAvailableFrom;
  attention: WireDataAvailableFrom;
};

export type WireJobRow = {
  id: string;
  job_id: string;
  title: string;
  client: string;
  state: WireJobState;
  shift_starts_at: WireIsoTimestamp;
  acts_at: WireIsoTimestamp | null;
  workers_available: WireNullableCount;
  /** The venue's zone, carried by every row including a still-watching one. */
  timezone: WireIanaTimezone;
};

export type WireWorkerRow = {
  worker_id: number;
  eligibility: WireWorkerEligibility;
  tier: string | null;
  score: number | null;
  distance_miles: number | null;
  favorite: boolean;
  shifts_with_client: number | null;
  selection_rank: number | null;
};

export type WireJobDetail = {
  id: string;
  job_id: string;
  title: string;
  client: string;
  shift_starts_at: WireIsoTimestamp;
  acts_at: WireIsoTimestamp | null;
  /** The venue's zone. Every instant on this detail renders on it, never on the browser clock. */
  timezone: WireIanaTimezone;
  state: WireJobState;
  run_meta: WireRunMeta;
  checks: WirePersistedCheck[];
  funnel: WireFunnel;
  funnel_stage: WireFunnelStage | null;
  /**
   * The recorded SELECTED wave, ordered by selection_rank ascending. Distinct from the paginated
   * after-gates snapshot on the workers endpoint, which is the complete population.
   * Typed as WireSelectedCandidate, so selection_rank is non-null by construction.
   * action_decision may still be null when a worker was selected but not yet acted on.
   */
  candidates: WireSelectedCandidate[];
  selected_worker_ids: number[];
  workers_available_total: WireNullableCount;
  exclusions: WireExclusionGroup[];
  timeline: WireTimelineEntry[];
  /**
   * This job's OWN attention history. Modal history must never be assembled by filtering
   * whichever page of the global attention endpoint happens to be loaded — that would silently
   * show a partial history whenever the operator was on page 2.
   */
  attention_events: WireAttentionEvent[];
  failure: { error_code: WireErrorCode; run_id: string } | null;
};

// -------------------------------------------------- named endpoint contracts

/** GET /console — everything the shell needs before any table loads. */
export type FirstResponderConsoleWire = {
  mode: WireModeState;
  safety: WireSafetyStatus;
  stats: WireHeadlineStats;
  filters: WireClientFilter[];
  results: WireResultsSummary;
  diagnostics: WireDiagnostics;
  policy_constraints: WirePolicyConstraints;
  data_available_from: WireDataBoundaries;
  /** null when the server cannot substantiate an attention count at all. */
  attention_count: WireNullableCount;
};

/** GET /console/jobs — paginated. */
export type FirstResponderJobsWire = WirePage<WireJobRow>;

/** GET /console/jobs/:id — one immutable recorded run. */
export type FirstResponderJobWire = { job: WireJobDetail };

/** GET /console/jobs/:id/workers — paginated after-gates snapshot. */
export type FirstResponderWorkersWire = WirePage<WireWorkerRow> & {
  candidates_total: WireNullableCount;
};

/** GET /console/attention — paginated. */
export type FirstResponderAttentionWire = WirePage<WireAttentionEvent> & {
  unresolved_count: WireNullableCount;
};

/** GET /console/policy — active policy, draft and server constraints. No audits: they paginate. */
export type FirstResponderPolicyWire = {
  active: WirePolicy | null;
  draft: WirePolicyDraft | null;
  constraints: WirePolicyConstraints;
};

/**
 * GET /console/policy/audits — the canonical paginated audit page.
 * Kept off the policy payload deliberately: an unbounded audit list embedded in a settings
 * response grows without limit and forces the whole policy card to re-fetch to page it.
 */
export type FirstResponderPolicyAuditsWire = WirePage<WirePolicyAudit>;

// ------------------------------------------------------------------ manifest

export const WIRE_ENDPOINTS = [
  'FirstResponderConsoleWire',
  'FirstResponderJobsWire',
  'FirstResponderJobWire',
  'FirstResponderWorkersWire',
  'FirstResponderAttentionWire',
  'FirstResponderPolicyWire',
  'FirstResponderPolicyAuditsWire',
] as const;
export type WireEndpoint = (typeof WIRE_ENDPOINTS)[number];

export const WIRE_PAGINATED_COLLECTIONS = [
  'jobs',
  'workers',
  'attention',
  'audits',
] as const;

export const WIRE_PAGINATION_KEYS = [
  'total',
  'page',
  'per_page',
  'total_pages',
] as const;

export const WIRE_FIELD_MANIFEST = {
  modes: WIRE_MODES,
  jobStates: WIRE_JOB_STATES,
  workerEligibilities: WIRE_WORKER_ELIGIBILITIES,
  actionDecisions: WIRE_ACTION_DECISIONS,
  exclusionKeys: WIRE_EXCLUSION_KEYS,
  checkKeys: WIRE_CHECK_KEYS,
  attentionReasonClasses: WIRE_ATTENTION_REASON_CLASSES,
  attentionResolutions: WIRE_ATTENTION_RESOLUTIONS,
  guardReasons: WIRE_GUARD_REASONS,
  systemAlertKeys: WIRE_SYSTEM_ALERT_KEYS,
  funnelStages: WIRE_FUNNEL_STAGES,
  timelineKeys: WIRE_TIMELINE_KEYS,
  errorCodes: WIRE_ERROR_CODES,
  safetyStates: WIRE_SAFETY_STATES,
  safetyClearBases: WIRE_SAFETY_CLEAR_BASES,
  policyStatuses: WIRE_POLICY_STATUSES,
} as const;
